import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronDown, Search } from 'lucide-react';
import { getProducts, getCategories } from '../lib/api';
import { optimize } from '../lib/imageOptimizer';
import ProductImg from '../components/ProductImg';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

/**
 * Search input with built-in debounce + clear button.
 * Updates the URL ?search= via the parent's onChange.
 */
function CatalogSearchBar({ value, onChange }) {
  const [local, setLocal] = useState(value || '');

  // Sync from URL changes (e.g. when navigating)
  useEffect(() => { setLocal(value || ''); }, [value]);

  // Debounce: 350ms after last keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      if (local !== (value || '')) onChange(local);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="relative mb-4" data-testid="catalog-search-bar">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Rechercher un maillot, un club, une équipe nationale…"
        className="w-full pl-10 pr-10 py-3 text-sm border border-zinc-200 focus:border-zinc-900 focus:outline-none text-zinc-900 placeholder:text-zinc-400"
        data-testid="catalog-search-input"
      />
      {local && (
        <button
          type="button"
          onClick={() => { setLocal(''); onChange(''); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-900"
          aria-label="Effacer"
          data-testid="catalog-search-clear"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

function ProductCard({ product, index }) {
  return (
    <Link
      to={`/produit/${product.slug}`}
      className={`product-card group border border-zinc-200 bg-white block opacity-0 animate-fade-in stagger-${(index % 8) + 1}`}
      data-testid={`catalog-product-${product.slug}`}
    >
      <div className="aspect-square overflow-hidden bg-zinc-50 relative">
        <ProductImg src={product.images[0]} alt={product.name} width={280} />
      </div>
      <div className="p-2.5">
        <p className="text-[10px] text-zinc-400 font-body uppercase tracking-wider mb-0.5">{product.team}</p>
        <h3 className="text-xs font-medium text-zinc-900 font-body leading-tight mb-1.5 line-clamp-2">{product.name}</h3>
        <span className="text-xs font-bold text-zinc-900 font-body">{product.price.toFixed(2)} €</span>
      </div>
    </Link>
  );
}

const FILTER_OPTIONS = {
  league: ["Ligue 1", "La Liga", "Premier League", "Bundesliga", "Serie A", "Brésil Série A", "MLS", "Sélections Nationales"],
  gender: [{ v: "homme", l: "Homme" }, { v: "femme", l: "Femme" }, { v: "enfant", l: "Enfant" }],
  version: [{ v: "fan", l: "Version Fan" }, { v: "joueur", l: "Version Joueur" }],
};

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = searchParams.get('category') || '';
  const subcategory = searchParams.get('subcategory') || '';
  const league = searchParams.get('league') || '';
  const team = searchParams.get('team') || '';
  const gender = searchParams.get('gender') || '';
  const version = searchParams.get('version') || '';
  const badge = searchParams.get('badge') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';
  const season = searchParams.get('season') || '';

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategories();
        setCategories(res.data.categories || []);
      } catch { /* ignore */ }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const params = { page, limit: 36, sort, light: true };
        if (category) params.category = category;
        if (subcategory) params.subcategory = subcategory;
        if (league) params.league = league;
        if (team) params.team = team;
        if (gender) params.gender = gender;
        if (version) params.version = version;
        if (badge) params.badge = badge;
        if (search) params.search = search;
        if (season) params.season = season;
        const res = await getProducts(params);
        setProducts(res.data.products || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      } catch (e) {
        console.error('[CatalogPage] Load failed:', e);
        setLoadError(true);
      }
      setLoading(false);
    };
    loadProducts();
  }, [category, subcategory, league, team, gender, version, badge, search, season, sort, page]);

  const setFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.delete('page');
    setSearchParams(params);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchParams({});
    setPage(1);
  };

  const activeFiltersCount = [category, subcategory, league, team, gender, version, badge, search, season].filter(Boolean).length;

  const title = team || (subcategory === 'Sélections Nationales' ? 'Coupe du Monde 2026' : '') || subcategory || category || (badge === 'nouveau' ? 'Nouveautés' : badge === 'best-seller' ? 'Best-sellers' : badge === 'promo' ? 'Promotions' : search ? `Recherche : ${search}` : 'Catalogue');

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="catalog-page">
      {/* Header */}
      <div className="mb-4">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-1 font-body">Collection</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight uppercase text-zinc-900">{title}</h1>
        <p className="text-sm text-zinc-500 font-body mt-1">{total} produit{total > 1 ? 's' : ''}</p>
      </div>

      {/* Search bar — visible & prominent */}
      <CatalogSearchBar value={search} onChange={(v) => setFilter('search', v)} />

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-4">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="btn-outline flex items-center gap-2 border border-zinc-200 px-4 py-2 text-sm font-body text-zinc-500 hover:text-zinc-900 hover:border-zinc-400"
          data-testid="filters-toggle"
        >
          <SlidersHorizontal size={14} />
          Filtres
          {activeFiltersCount > 0 && (
            <span className="bg-zinc-900 text-white text-[10px] font-bold px-1.5 py-0.5 ml-1">{activeFiltersCount}</span>
          )}
        </button>
        <Select value={sort} onValueChange={(v) => setFilter('sort', v)}>
          <SelectTrigger className="w-[180px] border-zinc-200 text-sm" data-testid="sort-select">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Plus récents</SelectItem>
            <SelectItem value="price_asc">Prix croissant</SelectItem>
            <SelectItem value="price_desc">Prix décroissant</SelectItem>
            <SelectItem value="name">Nom A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active filters */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="active-filters">
          {category && (
            <button onClick={() => setFilter('category', '')} className="flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-3 py-1 text-xs text-zinc-600 font-body">
              {category} <X size={12} />
            </button>
          )}
          {subcategory && (
            <button onClick={() => setFilter('subcategory', '')} className="flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-3 py-1 text-xs text-zinc-600 font-body">
              {subcategory} <X size={12} />
            </button>
          )}
          {league && (
            <button onClick={() => setFilter('league', '')} className="flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-3 py-1 text-xs text-zinc-600 font-body">
              {league} <X size={12} />
            </button>
          )}
          {team && (
            <button onClick={() => setFilter('team', '')} className="flex items-center gap-1 bg-zinc-900 text-white px-3 py-1 text-xs font-bold font-body">
              {team} <X size={12} />
            </button>
          )}
          {gender && (
            <button onClick={() => setFilter('gender', '')} className="flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-3 py-1 text-xs text-zinc-600 font-body">
              {gender} <X size={12} />
            </button>
          )}
          {version && (
            <button onClick={() => setFilter('version', '')} className="flex items-center gap-1 bg-zinc-100 border border-zinc-200 px-3 py-1 text-xs text-zinc-600 font-body">
              {version} <X size={12} />
            </button>
          )}
          <button onClick={clearFilters} className="text-xs text-[#FF3B30] font-body hover:underline">Effacer tout</button>
        </div>
      )}

      <div className="flex gap-6 min-w-0">
        {/* Filters sidebar */}
        {filtersOpen && (
          <aside className="hidden lg:block w-64 shrink-0 space-y-6 font-body" data-testid="filters-sidebar">
            {/* Categories */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Catégorie</h4>
              <div className="space-y-1">
                {categories.map(cat => (
                  <button
                    key={cat.slug}
                    onClick={() => setFilter('category', cat.name === category ? '' : cat.name)}
                    className={`block w-full text-left px-2 py-1.5 text-sm transition-colors ${cat.name === category ? 'text-zinc-900 bg-zinc-100 font-medium' : 'text-zinc-500 hover:text-zinc-900'}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
            {/* League */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Ligue</h4>
              <div className="space-y-1">
                {FILTER_OPTIONS.league.map(l => (
                  <button
                    key={l}
                    onClick={() => setFilter('league', l === league ? '' : l)}
                    className={`block w-full text-left px-2 py-1.5 text-sm transition-colors ${l === league ? 'text-zinc-900 bg-zinc-100 font-medium' : 'text-zinc-500 hover:text-zinc-900'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {/* Gender */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Genre</h4>
              <div className="space-y-1">
                {FILTER_OPTIONS.gender.map(g => (
                  <button
                    key={g.v}
                    onClick={() => setFilter('gender', g.v === gender ? '' : g.v)}
                    className={`block w-full text-left px-2 py-1.5 text-sm transition-colors ${g.v === gender ? 'text-zinc-900 bg-zinc-100 font-medium' : 'text-zinc-500 hover:text-zinc-900'}`}
                  >
                    {g.l}
                  </button>
                ))}
              </div>
            </div>
            {/* Version */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Version</h4>
              <div className="space-y-1">
                {FILTER_OPTIONS.version.map(v => (
                  <button
                    key={v.v}
                    onClick={() => setFilter('version', v.v === version ? '' : v.v)}
                    className={`block w-full text-left px-2 py-1.5 text-sm transition-colors ${v.v === version ? 'text-zinc-900 bg-zinc-100 font-medium' : 'text-zinc-500 hover:text-zinc-900'}`}
                  >
                    {v.l}
                  </button>
                ))}
              </div>
            </div>
          </aside>
        )}

        {/* Product grid */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
              {Array(10).fill(null).map((_, i) => (
                <div key={i} className="bg-zinc-100 aspect-[3/4] animate-pulse" />
              ))}
            </div>
          ) : loadError ? (
            <div className="text-center py-20" data-testid="catalog-load-error">
              <p className="text-amber-700 font-body text-base mb-3">Le chargement des produits a échoué.</p>
              <p className="text-zinc-500 text-sm mb-6">Vérifie ta connexion ou réessaie dans quelques secondes.</p>
              <button onClick={() => window.location.reload()} className="btn-primary bg-zinc-900 text-white font-bold text-sm px-6 py-3 uppercase tracking-wider">
                Réessayer
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-zinc-500 font-body text-lg mb-4">Aucun produit trouvé</p>
              <button onClick={clearFilters} className="btn-primary bg-zinc-900 text-white font-bold text-sm px-6 py-3 uppercase tracking-wider">
                Voir tout le catalogue
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>
              {/* Pagination */}
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
                  {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 text-sm font-body font-bold ${p === page ? 'bg-zinc-900 text-white' : 'border border-zinc-200 text-zinc-400 hover:text-zinc-900 hover:border-zinc-400'}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
